import { createFileRoute, Link } from "@tanstack/react-router";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import { formatBlogDate, listBlogPosts } from "@/lib/blog";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const TITLE = "AgentMRR Blog - Building and Selling AI Agents";
const DESCRIPTION =
	"Writing for founders shipping AI agents: what buyers actually price, why demos stall, and how verified revenue changes the conversation.";

export const Route = createFileRoute("/blog/")({
	component: BlogIndex,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/blog" });
		return {
			meta: [
				{ title: TITLE },
				{ name: "description", content: DESCRIPTION },
				{ property: "og:title", content: TITLE },
				{ property: "og:description", content: DESCRIPTION },
				{ property: "og:image", content: ogImageUrl("blog") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("blog") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/blog" }),
		};
	},
});

function BlogIndex() {
	const posts = listBlogPosts();

	return (
		<PublicPageLayout>
			<div className="mx-auto w-full max-w-3xl py-10">
				<h1
					className="font-mono text-[clamp(1.5rem,3vw,2rem)] leading-[1.1] tracking-[-0.02em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					Blog
				</h1>
				<p className="mt-3 max-w-xl text-base leading-[1.55] text-muted-foreground">
					{DESCRIPTION}
				</p>

				<ul className="mt-8 divide-y divide-border border border-border bg-card">
					{posts.map((post) => (
						<li key={post.slug}>
							<Link
								to="/blog/$slug"
								params={{ slug: post.slug }}
								className="block p-5 transition-colors hover:bg-background"
								lang={post.lang ?? "en"}
							>
								<p className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
									{formatBlogDate(post.published, post.lang)} &middot;{" "}
									{post.readingMinutes} min
								</p>
								<p className="mt-2 font-mono text-lg leading-[1.25] text-foreground">
									{post.title}
								</p>
								<p className="mt-2 text-base leading-[1.55] text-muted-foreground">
									{post.description}
								</p>
							</Link>
						</li>
					))}
				</ul>
			</div>

			<PublicFooter />
		</PublicPageLayout>
	);
}
